import { type FC, type MouseEvent, type SyntheticEvent, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import cn from 'classnames';

import type { PostMediaImage } from './PostMediaFrame';

interface LightboxImageProps {
  image: PostMediaImage;
  isZoomed: boolean;
  onZoomChange: (isZoomed: boolean) => void;
  onDismiss: () => void;
}

export const LightboxImage: FC<LightboxImageProps> = ({ image, isZoomed, onZoomChange, onDismiss }) => {
  const { t } = useTranslation();
  const viewportRef = useRef<HTMLDivElement>(null);
  const [isFullLoaded, setIsFullLoaded] = useState(image.src === image.fullSrc);
  const [canZoom, setCanZoom] = useState(false);

  const measureZoom = ({ currentTarget }: SyntheticEvent<HTMLImageElement>) =>
    setCanZoom(currentTarget.naturalWidth > currentTarget.width || currentTarget.naturalHeight > currentTarget.height);

  const toggleZoom = (event: MouseEvent<HTMLButtonElement>) => {
    // Safari does not focus a clicked button, and the arrow keys pan from the focused element.
    event.currentTarget.focus({ preventScroll: true });

    if (isZoomed) {
      onZoomChange(false);
      return;
    }

    const frame = event.currentTarget.getBoundingClientRect();
    const isPointer = event.detail > 0;
    const horizontal = isPointer ? (event.clientX - frame.left) / frame.width : 0.5;
    const vertical = isPointer ? (event.clientY - frame.top) / frame.height : 0.5;

    // The viewport has to lay out the zoomed image before the pressed point can be centered.
    flushSync(() => onZoomChange(true));
    const viewport = viewportRef.current;
    viewport?.scrollTo({
      left: horizontal * viewport.scrollWidth - viewport.clientWidth / 2,
      top: vertical * viewport.scrollHeight - viewport.clientHeight / 2,
    });
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: pressing the empty area around the image dismisses; keyboard users have the close button and Escape
    // biome-ignore lint/a11y/useKeyWithClickEvents: same as above
    <div
      ref={viewportRef}
      data-testid="lightbox-viewport"
      className="flex size-full overflow-auto"
      onClick={(event) => {
        if (event.target === event.currentTarget) onDismiss();
      }}
    >
      <button
        type="button"
        disabled={!canZoom}
        aria-label={t(isZoomed ? 'community.post.lightbox.zoom_out' : 'community.post.lightbox.zoom_in')}
        onClick={toggleZoom}
        className={cn('m-auto shrink-0', { 'cursor-zoom-in': canZoom && !isZoomed, 'cursor-zoom-out': isZoomed })}
      >
        {/* The feed rendition is already cached, so it shows at once while the larger one loads. */}
        <img
          src={isFullLoaded ? image.fullSrc : image.src}
          alt={image.alt}
          referrerPolicy="no-referrer"
          onLoad={isFullLoaded ? measureZoom : undefined}
          className={cn('block', {
            'max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)]': !isZoomed,
            'max-w-none': isZoomed,
          })}
        />
      </button>
      {!isFullLoaded && (
        <img
          src={image.fullSrc}
          alt=""
          referrerPolicy="no-referrer"
          className="hidden"
          onLoad={() => setIsFullLoaded(true)}
        />
      )}
    </div>
  );
};
